import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import styles from './SortBy.module.scss';
import { defaultSortOption, findOption, sortOptions } from '../../util/sortOptions';
import ChevronDown from './chevron-down.svg';

interface ISortBy {
  className?: string;
}

const SortBy = ({
  className = ''
}: ISortBy) => {
  const router = useRouter();
  const [sortByMenuVisibility, setSortByMenuVisibility] = useState(false);

  const activeSortOption = useMemo(() => {
    if (router.asPath.includes('sort')) {
      const activeOption = findOption(router.query.sort as string);
      if (activeOption) {
        return activeOption;
      }
    }
    return defaultSortOption;
  }, [router.query]);

  const handleSortOptionSelection = (option: string) => {
    setSortByMenuVisibility(false);

    // The default sort option should remove the `sort` parameter from the query string.
    if (option === defaultSortOption.sort) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { sort, ...query } = router.query;
      router.push({ query });
    } else {
      router.push({
        query: {
          ...router.query,
          sort: option
        }
      });
    }
  };

  useEffect(() => {
    const onWindowClick = () => {
      setSortByMenuVisibility(false);
    };

    if (sortByMenuVisibility) {
      window.addEventListener('click', onWindowClick);
    }

    return () => {
      window.removeEventListener('click', onWindowClick);
    };
  }, [sortByMenuVisibility]);

  return (
    <div className={`${styles.SortBy} ${className}`}>
      <button
        className={classNames(styles.SortByButton, {
          [styles.SortByMenuVisible]: sortByMenuVisibility,
          [styles.SortByMenuHidden]: !sortByMenuVisibility,
        })}
        type="button"
        onClick={() => setSortByMenuVisibility(true)}
      >
        {activeSortOption ? `Sort By: ${activeSortOption.label}` : 'Sort By'}
        <ChevronDown />
      </button>
      <ul onMouseLeave={() => setSortByMenuVisibility(false)}>
        {sortOptions.map((option) => (
          <li key={option.sort}>
            <button
              onClick={() => handleSortOptionSelection(option.sort)}
              type="button"
              className={
                activeSortOption.sort === option.sort
                  ? `${styles.SortByOption} ${styles.Active}`
                  : styles.SortByOption
              }
            >
              Sort By:&nbsp;{option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SortBy;
